/*!
 * HolographicEffect
 * Cursor / touch-drag / gyroscope reactive 3D tilt + holographic rainbow
 * sheen for a product image, confined (optionally) to a masked region.
 *
 * Requires holographic-effect.css to be included on the page as well.
 *
 * -----------------------------------------------------------------------
 * BASIC USAGE
 * -----------------------------------------------------------------------
 *   <div class="product-gallery__media">
 *     <img src="product.png" alt="Product">
 *   </div>
 *
 *   <link rel="stylesheet" href="holographic-effect.css">
 *   <script src="holographic-effect.js"></script>
 *   <script>
 *     HolographicEffect.init('.product-gallery__media');
 *   </script>
 *
 * Target elements can be a CSS selector string, a single element, or a
 * NodeList/array of elements. Each match must contain exactly one <img> —
 * that's the image the effect wraps. init() returns an array of the
 * created instances.
 *
 * -----------------------------------------------------------------------
 * OPTIONS
 * -----------------------------------------------------------------------
 * Pass as a second argument to init()/the constructor, e.g.
 *   HolographicEffect.init('.product-gallery__media', { tiltMax: 12 });
 * or set per-element via data-holo-* attributes (handy when different
 * products need different settings but share one init() call):
 *   <div class="product-gallery__media" data-holo-mask="logo">
 *
 * A JS option always wins over a data-attribute if both are set for the
 * same element.
 *
 *   option            data-attribute              default   notes
 *   ----------------  --------------------------  --------  --------------------------------------------
 *   mask              data-holo-mask               null      Name of a mask preset defined in
 *                                                             holographic-effect.css as ".holo-mask--<name>"
 *                                                             (e.g. mask: 'logo' -> .holo-mask--logo). Adds
 *                                                             that class to the card, confining the effect
 *                                                             to whatever shape its --holo-mask-image is.
 *                                                             You can add the class directly in markup
 *                                                             instead of using this option if you prefer.
 *                                                             Omit entirely to apply the effect across the
 *                                                             whole image.
 *   maskSrc           data-holo-mask-src           null      Escape hatch for a one-off mask you don't want
 *                                                             to define as a CSS preset — a raw URL or data
 *                                                             URI, same rules as a preset (same size/framing
 *                                                             as the product image, alpha-only). Takes
 *                                                             priority over `mask` if both are set.
 *   maskCenter        data-holo-mask-center        "50% 50%" Centre point the rainbow sweeps around. Tweak
 *                                                             if your product's true visual centre isn't
 *                                                             the middle of the image (e.g. an off-centre
 *                                                             circular lid).
 *   tiltMax           data-holo-tilt-max           8         Max tilt rotation (deg) for mouse/touch.
 *   tiltMaxGyro       data-holo-tilt-gyro          20        Max tilt rotation (deg) for gyroscope on
 *                                                             mobile — independent of tiltMax.
 *   orientationRange  data-holo-orient-range       10        Degrees of physical phone tilt needed to reach
 *                                                             the full effect. Lower = more sensitive.
 *   scaleActive       data-holo-scale              1.075     Hover/active zoom multiplier.
 *   bandCycle         data-holo-band-cycle         50        % of the full circle per rainbow repeat. Must
 *                                                             divide evenly into 100 (e.g. 20, 25, 50, 100)
 *                                                             or the pattern will show a seam where it wraps.
 *   colors            (JS only)                    7 colours Array of CSS colour strings for the rainbow,
 *                                                             evenly spaced and seamlessly looped automatically.
 *   opacityFill       data-holo-opacity-fill       0.55      Opacity of the solid-colour (normal blend) layer.
 *   opacityShine      data-holo-opacity-shine      0.3       Opacity of the color-dodge shine layer.
 *   opacitySparkle    data-holo-opacity-sparkle    0.5       Opacity of the glitter-grain layer.
 *   opacityGlare      data-holo-opacity-glare      1         Opacity of the cursor-following glare highlight.
 *   sparkle           data-holo-sparkle            true      Set to false/"false" to omit the grain layer.
 *   enableGyro        data-holo-gyro               true      Set to false/"false" to disable gyroscope input.
 *   tiltButtonLabel    data-holo-tilt-label         "Unlock the shine"  Label for the iOS permission button.
 *
 * -----------------------------------------------------------------------
 * INSTANCE API
 * -----------------------------------------------------------------------
 *   const [instance] = HolographicEffect.init('.product-gallery__media');
 *   instance.destroy();      // removes listeners/DOM changes for that instance
 *   instance.setMask('logo'); // swap to a different .holo-mask--<name> preset
 *                             // at runtime; pass null/'' to remove masking
 *
 * -----------------------------------------------------------------------
 * MASK PRESETS (defined in holographic-effect.css)
 * -----------------------------------------------------------------------
 * Rather than passing a raw image URL from JS, the recommended way to mask
 * the effect to a region (e.g. just a logo) is to define a named preset in
 * the CSS file:
 *
 *   .holo-mask--logo {
 *     --holo-mask-image: url("data:image/png;base64,...");
 *   }
 *
 * ...and then select it either in markup or via the `mask` option/
 * data-holo-mask attribute documented above. This keeps mask image data
 * (which is often a large base64 string) out of your JS/HTML and in one
 * place designers can add to without touching the plugin's code. The
 * `maskSrc` option/data-holo-mask-src attribute remains as an escape hatch
 * for a one-off mask you don't want to add as a preset.
 *
 * Whichever way a mask is supplied, it must be the same pixel dimensions
 * and framing as the product image it's paired with — fully transparent
 * everywhere except the shape you want to glow (any solid colour; only
 * its alpha channel is read). It's never displayed, only read as a
 * stencil. If the product image's aspect ratio or crop changes, the mask
 * needs to be regenerated to match, or the glow will drift out of
 * alignment with the artwork.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.HolographicEffect = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DEFAULT_COLORS = [
    'rgb(255, 0, 120)',
    'rgb(255, 165, 0)',
    'rgb(255, 240, 0)',
    'rgb(0, 220, 130)',
    'rgb(0, 190, 255)',
    'rgb(130, 80, 255)',
    'rgb(255, 0, 180)'
  ];

  // Gyroscope permission is page-wide on iOS (one prompt covers every
  // instance), so it's tracked here rather than per instance. Any instance
  // still waiting when permission is granted gets started automatically
  // and has its own "Unlock the shine" button removed.
  var gyro = {
    supported: typeof DeviceOrientationEvent !== 'undefined',
    needsPermission: typeof DeviceOrientationEvent !== 'undefined'
      && typeof DeviceOrientationEvent.requestPermission === 'function',
    granted: false,
    waiting: []
  };

  function requestGyroPermission() {
    if (!gyro.needsPermission) return Promise.resolve(true);
    return DeviceOrientationEvent.requestPermission()
      .then(function (state) {
        var ok = state === 'granted';
        if (ok) grantGyro();
        return ok;
      })
      .catch(function () {
        return false;
      });
  }

  function grantGyro() {
    gyro.granted = true;
    gyro.waiting.forEach(function (fn) { fn(); });
    gyro.waiting.length = 0;
  }

  function clamp01(v) {
    return Math.min(Math.max(v, 0), 1);
  }

  function readBool(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    return value !== 'false' && value !== false;
  }

  function readNum(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function pick(optionValue, dataValue, fallback, reader) {
    if (optionValue !== undefined) return reader ? reader(optionValue, fallback) : optionValue;
    return reader ? reader(dataValue, fallback) : (dataValue !== undefined ? dataValue : fallback);
  }

  function HolographicEffect(el, options) {
    if (!el) throw new Error('HolographicEffect: no element provided');
    if (el.holographicEffect) return el.holographicEffect;

    var data = el.dataset || {};
    var opts = options || {};

    this.el = el;
    this.mask = pick(opts.mask, data.holoMask, null);
    this.maskSrc = pick(opts.maskSrc, data.holoMaskSrc, null);
    this.maskCenter = pick(opts.maskCenter, data.holoMaskCenter, '50% 50%');
    this.tiltMax = pick(opts.tiltMax, data.holoTiltMax, 8, readNum);
    this.tiltMaxGyro = pick(opts.tiltMaxGyro, data.holoTiltGyro, 20, readNum);
    this.orientationRange = pick(opts.orientationRange, data.holoOrientRange, 10, readNum);
    this.scaleActive = pick(opts.scaleActive, data.holoScale, 1.025, readNum);
    this.bandCycle = pick(opts.bandCycle, data.holoBandCycle, 50, readNum);
    this.colors = opts.colors || DEFAULT_COLORS;
    this.opacityFill = pick(opts.opacityFill, data.holoOpacityFill, 0.55, readNum);
    this.opacityShine = pick(opts.opacityShine, data.holoOpacityShine, 0.3, readNum);
    this.opacitySparkle = pick(opts.opacitySparkle, data.holoOpacitySparkle, 0.5, readNum);
    this.opacityGlare = pick(opts.opacityGlare, data.holoOpacityGlare, 1, readNum);
    this.sparkle = pick(opts.sparkle, data.holoSparkle, true, readBool);
    this.enableGyro = pick(opts.enableGyro, data.holoGyro, true, readBool);
    this.tiltButtonLabel = pick(opts.tiltButtonLabel, data.holoTiltLabel, 'Unlock the shine');

    this._orientationBaseline = null;
    this._tiltBtn = null;
    this._boundHandleMove = this._handleMove.bind(this);
    this._boundActivate = this._activate.bind(this);
    this._boundDeactivate = this._deactivate.bind(this);
    this._boundHandleOrientation = this._handleOrientation.bind(this);
    this._boundHandleTouchMove = (function (self) {
      return function (e) { self._handleMove(e.touches[0]); };
    })(this);

    this._build();
    this._bindEvents();

    el.holographicEffect = this;
  }

  HolographicEffect.prototype._build = function () {
    var el = this.el;
    var img = el.querySelector('img');
    if (!img) {
      throw new Error('HolographicEffect: target element has no <img> inside it');
    }

    el.classList.add('holo-scene');

    var card = document.createElement('div');
    card.className = 'holo-card';

    // Insert the wrapper where the img currently is, then move the img
    // (not a clone) inside it — preserves any existing attributes,
    // srcset, lazy-load state, or listeners already on the img.
    el.insertBefore(card, img);
    img.classList.add('holo-image');
    card.appendChild(img);

    var holoFill = document.createElement('div');
    holoFill.className = 'holo-fill';

    var glare = document.createElement('div');
    glare.className = 'holo-glare';

    var holo = document.createElement('div');
    holo.className = 'holo-shine';

    card.appendChild(holoFill);
    card.appendChild(glare);
    card.appendChild(holo);

    var sparkle = null;
    if (this.sparkle) {
      sparkle = document.createElement('div');
      sparkle.className = 'holo-sparkle';
      card.appendChild(sparkle);
    }

    this.card = card;
    this.img = img;
    this.holoFill = holoFill;
    this.holo = holo;
    this.glare = glare;
    this.sparkleEl = sparkle;

    this._applyGradient();
    this._applyMaskPreset(this.mask);
    this._applyMaskSrc();
    this._applyOpacityVars();
    this._preserveSizing();
  };

  // Moving the <img> to position:absolute (so the overlay layers can sit
  // on top of it) pulls it out of normal document flow. Some themes size
  // the target element entirely from the img's own rendered box (no
  // explicit height/aspect-ratio of their own) — for those, removing the
  // img from flow collapses the container to 0 height, making the whole
  // thing (photo included) disappear even though it's still in the DOM.
  //
  // This detects that collapse and restores real height using an
  // aspect-ratio, without touching anything for themes that already size
  // the container correctly on their own. The ratio is read from the
  // img's width/height HTML attributes first (synchronous, no need to
  // wait for the image to actually load) rather than naturalWidth/
  // naturalHeight — important for loading="lazy" images, since a
  // collapsed 0-height container can prevent some lazy-load
  // implementations from ever considering the image "in view", which
  // would stop it from loading at all rather than just delaying it.
  HolographicEffect.prototype._preserveSizing = function () {
    var el = this.el;
    var img = this.img;

    var setRatioFromAttrs = function () {
      var w = parseFloat(img.getAttribute('width'));
      var h = parseFloat(img.getAttribute('height'));
      if (w && h) {
        el.style.aspectRatio = w + ' / ' + h;
        return true;
      }
      return false;
    };

    var setRatioFromNatural = function () {
      if (img.naturalWidth && img.naturalHeight) {
        el.style.aspectRatio = img.naturalWidth + ' / ' + img.naturalHeight;
        return true;
      }
      return false;
    };

    if (el.getBoundingClientRect().height > 1) return; // already sized, leave it alone

    if (setRatioFromAttrs()) return;
    if (setRatioFromNatural()) return;

    // Neither was available yet (no width/height attributes, and the
    // image hasn't loaded) — fall back to waiting for load.
    img.addEventListener('load', function () {
      if (el.getBoundingClientRect().height <= 1) setRatioFromNatural();
    }, { once: true });
  };

  // Builds N+1 colour stops (the +1 repeats the first colour) spaced
  // evenly across bandCycle%, so the pattern always loops seamlessly
  // regardless of how many colours are passed in.
  HolographicEffect.prototype._colorStops = function () {
    var colors = this.colors;
    var cycle = this.bandCycle;
    var step = cycle / colors.length;
    var stops = colors.map(function (c, i) {
      return c + ' ' + (i * step).toFixed(2) + '%';
    });
    stops.push(colors[0] + ' ' + cycle + '%');
    return stops.join(',\n      ');
  };

  HolographicEffect.prototype._applyGradient = function () {
    var gradient = 'repeating-conic-gradient(from var(--holo-angle, 0deg) at '
      + this.maskCenter + ', ' + this._colorStops() + ')';
    this.holoFill.style.backgroundImage = gradient;
    this.holo.style.backgroundImage = gradient;
  };

  // Selects a ".holo-mask--<name>" preset defined in holographic-effect.css
  // by adding that class to the card (removing whichever preset class was
  // there before, if any). The actual mask image data lives entirely in
  // CSS via that class's --holo-mask-image custom property — this method
  // never touches image data directly, just swaps a class name.
  HolographicEffect.prototype._applyMaskPreset = function (name) {
    if (this._maskClass) {
      this.card.classList.remove(this._maskClass);
      this._maskClass = null;
    }
    if (name) {
      this._maskClass = 'holo-mask--' + name;
      this.card.classList.add(this._maskClass);
    }
    this.mask = name || null;
  };

  // Swap the active mask preset at runtime, e.g. instance.setMask('logo')
  // or instance.setMask(null) to remove masking (whole image gets the
  // effect). Doesn't affect maskSrc, if one was also set — that always
  // takes priority since it's a direct inline style.
  HolographicEffect.prototype.setMask = function (name) {
    this._applyMaskPreset(name);
  };

  // Escape hatch for a one-off mask supplied as a raw URL/data URI rather
  // than a CSS preset. Applied as a direct inline style, which takes
  // priority over any --holo-mask-image inherited from a preset class.
  HolographicEffect.prototype._applyMaskSrc = function () {
    if (!this.maskSrc) return;
    var targets = [this.holoFill, this.holo, this.glare, this.sparkleEl].filter(Boolean);
    targets.forEach(function (elm) {
      elm.style.webkitMaskImage = 'url("' + this.maskSrc + '")';
      elm.style.maskImage = 'url("' + this.maskSrc + '")';
      elm.style.webkitMaskRepeat = 'no-repeat';
      elm.style.maskRepeat = 'no-repeat';
      elm.style.webkitMaskSize = 'contain';
      elm.style.maskSize = 'contain';
      elm.style.webkitMaskPosition = 'center';
      elm.style.maskPosition = 'center';
    }, this);
  };

  HolographicEffect.prototype._applyOpacityVars = function () {
    this.card.style.setProperty('--holo-opacity-fill', this.opacityFill);
    this.card.style.setProperty('--holo-opacity-shine', this.opacityShine);
    this.card.style.setProperty('--holo-opacity-sparkle', this.opacitySparkle);
    this.card.style.setProperty('--holo-opacity-glare', this.opacityGlare);
  };

  // Shared by mouse, touch and device-orientation input: px/py are the
  // 0..1 position within the card (or its gyroscope equivalent). tiltMax
  // is passed explicitly so gyro can use a different amount than
  // mouse/touch without any other code branching.
  HolographicEffect.prototype._applyEffect = function (px, py, tiltMax) {
    var rotateY = (px - 0.5) * tiltMax * 2;
    var rotateX = (0.5 - py) * tiltMax * 2;

    this.card.style.transform =
      'rotateX(' + rotateX + 'deg) rotateY(' + rotateY + 'deg) scale(' + this.scaleActive + ')';

    // Rotate the conic rainbow sweep to match the cursor/tilt's angular
    // position around the card's centre. The masked shape is typically a
    // curved ring/logo, and a conic gradient's hue changes at an even rate
    // all the way around that curve — unlike a straight linear gradient
    // projected onto it, which changes hue unevenly and can look like a
    // hard jump between two colours at some angles.
    var angle = Math.atan2(py - 0.5, px - 0.5) * (180 / Math.PI);
    this.holo.style.setProperty('--holo-angle', angle + 'deg');
    this.holoFill.style.setProperty('--holo-angle', angle + 'deg');

    this.glare.style.background = 'radial-gradient(circle at '
      + (px * 100) + '% ' + (py * 100) + '%, rgba(255,255,255,0.6) 0%, '
      + 'rgba(255,255,255,0.15) 30%, transparent 60%)';
  };

  HolographicEffect.prototype._handleMove = function (e) {
    var rect = this.el.getBoundingClientRect();
    var px = clamp01((e.clientX - rect.left) / rect.width);
    var py = clamp01((e.clientY - rect.top) / rect.height);
    this._applyEffect(px, py, this.tiltMax);
  };

  HolographicEffect.prototype._activate = function () {
    this.card.classList.add('is-active');
  };

  HolographicEffect.prototype._deactivate = function () {
    this.card.classList.remove('is-active');
    this.card.style.transform = 'rotateX(0deg) rotateY(0deg) scale(1)';
  };

  HolographicEffect.prototype._handleOrientation = function (e) {
    if (e.beta === null || e.gamma === null) return;
    if (this._orientationBaseline === null) {
      this._orientationBaseline = { beta: e.beta, gamma: e.gamma };
    }
    var dBeta = e.beta - this._orientationBaseline.beta;
    var dGamma = e.gamma - this._orientationBaseline.gamma;

    var px = clamp01(0.5 + dGamma / (this.orientationRange * 2));
    var py = clamp01(0.5 + dBeta / (this.orientationRange * 2));

    this._activate();
    this._applyEffect(px, py, this.tiltMaxGyro);
  };

  HolographicEffect.prototype._startOrientation = function () {
    window.addEventListener('deviceorientation', this._boundHandleOrientation);
  };

  HolographicEffect.prototype._addTiltButton = function () {
    if (this._tiltBtn) return;
    var self = this;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'holo-tilt-btn';
    btn.textContent = this.tiltButtonLabel;
    btn.addEventListener('click', function () {
      requestGyroPermission();
    });
    this.el.appendChild(btn);
    this._tiltBtn = btn;
    void self;
  };

  HolographicEffect.prototype._removeTiltButton = function () {
    if (this._tiltBtn) {
      this._tiltBtn.remove();
      this._tiltBtn = null;
    }
  };

  HolographicEffect.prototype._bindEvents = function () {
    var el = this.el;
    el.addEventListener('mouseenter', this._boundActivate);
    el.addEventListener('mousemove', this._boundHandleMove);
    el.addEventListener('mouseleave', this._boundDeactivate);

    // Touch-drag: finger must be down and moving over the element.
    el.addEventListener('touchstart', this._boundActivate, { passive: true });
    el.addEventListener('touchmove', this._boundHandleTouchMove, { passive: true });
    el.addEventListener('touchend', this._boundDeactivate);

    if (!this.enableGyro || !gyro.supported) return;

    if (gyro.granted) {
      this._startOrientation();
    } else if (gyro.needsPermission) {
      // iOS 13+ — needs a tap before sensors can be read. Any instance's
      // button grants permission for every instance on the page.
      this._addTiltButton();
      var self = this;
      gyro.waiting.push(function () {
        self._startOrientation();
        self._removeTiltButton();
      });
    } else {
      // Android and others — no explicit permission needed.
      this._startOrientation();
    }
  };

  HolographicEffect.prototype.destroy = function () {
    var el = this.el;
    el.removeEventListener('mouseenter', this._boundActivate);
    el.removeEventListener('mousemove', this._boundHandleMove);
    el.removeEventListener('mouseleave', this._boundDeactivate);
    el.removeEventListener('touchstart', this._boundActivate);
    el.removeEventListener('touchmove', this._boundHandleTouchMove);
    el.removeEventListener('touchend', this._boundDeactivate);
    window.removeEventListener('deviceorientation', this._boundHandleOrientation);
    this._removeTiltButton();
    delete el.holographicEffect;
  };

  HolographicEffect.init = function (target, options) {
    var els;
    if (typeof target === 'string') {
      els = Array.prototype.slice.call(document.querySelectorAll(target));
    } else if (target && typeof target.length === 'number') {
      els = Array.prototype.slice.call(target);
    } else {
      els = [target];
    }
    return els.map(function (el) { return new HolographicEffect(el, options); });
  };

  // Exposed for advanced integrations that want to trigger the iOS
  // permission prompt from their own UI instead of the auto-injected
  // per-instance button.
  HolographicEffect.requestTiltPermission = requestGyroPermission;
  HolographicEffect.defaultColors = DEFAULT_COLORS;

  return HolographicEffect;
});
